import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useAWSConnect } from '../../app/context/ConnectStreamsContext'
import { useIncomingCall } from '../../hooks/useIncomingCall'
import { callLogger } from '../../Utils/ccpLogger'
import { parseCallHeaderCode } from '../../services/incomingCallHandledState'
import { postSetCallEndedByInterpreter } from '../../services/setCallEndedByInterpreter'
import { useAuth } from '../../app/modules/auth'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import {
    Phone,
    PhoneOff,
    MicOff,
    Mic,
    CheckCircle,
    Headphones,
    Pause,
    Play,
    Users,
    UserPlus,
    ChevronDown,
    ChevronUp,
    X,
    NotebookText,
    CircleX,
    Grip,
    Delete,
    Info,
    PhoneForwarded,
    Settings,
    WifiOff,
} from 'lucide-react'
import './ConnectStreamsWidget.css'
import { PhoneInput, defaultCountries, parseCountry } from 'react-international-phone'
import { useWidgetDockSide, writeWidgetDockSide } from './widgetDockPreference'
import 'react-international-phone/style.css'
import ConnectLoader from './connect-loader'
import { VideoCallPanel } from './VideoCallPanel'
import { ConnectWidgetSettings } from './ConnectWidgetSettings'
import { ConnectAccountMismatchPanel } from './ConnectAccountMismatchPanel'
import { ConnectIdleShell, getConnectIdleAuthPhase } from './ConnectIdleShell'
import { PillProgressRing, usePillRingState } from './PillProgressRing'
import { ConnectConnectionFailedPanel } from './ConnectConnectionFailedPanel'
import {
    ConnectSoftphoneRecoveryPanel,
    type SoftphoneProbePhase,
} from './ConnectSoftphoneRecoveryPanel'
import { hasConnectEmailMismatch } from '../../Utils/connectAccountMatch'
import { isMobilePhone } from '../../Utils/isMobilePhone'
import { validateDialNumber, friendlyDialError } from '../../Utils/phoneDialGuard'
import { startProbeCooldown } from '../../services/softphoneRecoveryLock'
import { useLazyGetSoftphoneProbeConfigQuery } from '../../app/redux/api'
import {
    CALL_A11Y,
    CCP_FOCUS_REQUEST_EVENT,
    buildCallConnectedAnnouncement,
    buildConnectingCallAnnouncement,
    buildHoldToggleAnnouncement,
    buildIncomingCallAnnouncement,
    buildMuteToggleAnnouncement,
    isAcceptCallShortcut,
    isCallInfoShortcut,
    isConferenceCallShortcut,
    isDeclineCallShortcut,
    isEndCallShortcut,
    isHoldCallShortcut,
    isKeypadCallShortcut,
    isMuteCallShortcut,
    isTypingTarget,
} from './callAccessibility'
import { getConnectStatusColor, getConnectStatusLabel } from '../../hooks/useConnectTabElection'
import toaster from '../../Utils/toaster'
import { streamsStore } from '../../services/streamsStore'
import { isMicrophoneUnavailable } from '../../services/softphoneRecovery'
import { ClampWithTooltip } from './ClampWithTooltip'

const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const isCallStatus = (status: string): boolean => {
    if (!status || typeof status !== 'string') return false
    const callStatuses = ['Busy', 'CallingCustomer', 'PendingBusy', 'On call', 'Connected', 'Connecting']
    return callStatuses.some(s => status.toLowerCase().includes(s.toLowerCase()))
}

const ACW_AUTO_CLOSE_SECONDS = 30
const NETWORK_DROP_NOTICE_MS = 10_000
const NO_INTERNET_VIDEO_URL = '/media/other/NoInternet.webm'

const KEYPAD_SUBS: Record<string, string> = {
    '2': 'ABC', '3': 'DEF', '4': 'GHI', '5': 'JKL',
    '6': 'MNO', '7': 'PQRS', '8': 'TUV', '9': 'WXYZ', '0': '+'
}

const KeypadButton = React.memo(({ digit, onPress }: { digit: string; onPress: (digit: string) => void }) => {
    const digitLabel =
        digit === '*' ? 'Star' : digit === '#' ? 'Pound' : `Digit ${digit}`
    return (
        <button
            type="button"
            className="cw-keypad-btn"
            aria-label={digitLabel}
            onPointerDown={(e) => { e.preventDefault(); onPress(digit); }}
        >
            <div className="cw-keypad-digit" aria-hidden="true">{digit}</div>
            {KEYPAD_SUBS[digit] && <div className="cw-keypad-sub" aria-hidden="true">{KEYPAD_SUBS[digit]}</div>}
        </button>
    )
})

export const ConnectWidget: React.FC = () => {
    const { currentUser } = useAuth()
    const [toggleExpand, setToggleExpand] = useState(true)
    const [isLoggingIn, setIsLoggingIn] = useState(false)

    // UI View States
    const [activeView, setActiveView] = useState<'none' | 'keypad' | 'conference'>('none')
    const [isSettingsOpen, setIsSettingsOpen] = useState(false)
    // Shared with the call modals / pills so they can sit on the opposite side.
    const dockSide = useWidgetDockSide()
    const [dtmfInput, setDtmfInput] = useState('')
    const [conferenceNumber, setConferenceNumber] = useState('')
    const [conferenceError, setConferenceError] = useState<string | null>(null)
    const [isEndCallConfirmOpen, setIsEndCallConfirmOpen] = useState(false)
    const [isEndingCall, setIsEndingCall] = useState(false)
    const [isMismatchSigningOut, setIsMismatchSigningOut] = useState(false)
    const [networkStatusVideoSrc, setNetworkStatusVideoSrc] = useState<string | null>(null)

    // Call Info Accordion State
    const [isCallInfoOpen, setIsCallInfoOpen] = useState(false)

    // Video panel visibility
    const [isVideoOpen, setIsVideoOpen] = useState(false)

    // Screen reader live region + keyboard focus targets
    const [liveA11yAnnouncement, setLiveA11yAnnouncement] = useState('')
    const acceptCallButtonRef = useRef<HTMLButtonElement | null>(null)
    const panelRef = useRef<HTMLDivElement | null>(null)
    const muteButtonRef = useRef<HTMLButtonElement | null>(null)
    const endCallConfirmActionRef = useRef<HTMLButtonElement | null>(null)
    const wasConnectingRef = useRef(false)

    // ACW: countdown for auto-close (30s), hourglass drains right → left
    const [acwSecondsRemaining, setAcwSecondsRemaining] = useState<number | null>(null)
    const acwIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    // Track if this terp was connected (to prevent late SignalR overlay after quick disconnect)
    const wasConnectedRef = useRef(false)

    // Context values
    const {
        isInitialized,
        isLoggedIn,
        ccpAuthState,
        agentName,
        agentEmail,
        agentStatus,
        isAgentMuted,
        contact,
        isConnected,
        isCallOnHold,
        callDuration,
        setAgentAvailable,
        setAgentOffline,
        endCall,
        clearContact,
        toggleAgentMute,
        toggleCallHold,
        openLoginPopup,
        reauthenticate,
        logout,
        activeCallData,
        isIncomingCall,
        acceptCall,
        rejectCall,
        isConnectionFailed,
        videoCapabilities,
        videoConnectionInfo,
        isMasterTab,
        syncedCallActive,
        syncedAgentState,
        publishCallActive,
        publishAgentState,
        requestMasterFocus,
        voiceEnhancementMode,
        setVoiceEnhancementMode,
        canTransferContact,
        microphoneDeviceId,
        speakerDeviceId,
        microphones,
        speakers,
        refreshAudioDevices,
        setMicrophoneDevice,
        setSpeakerDevice,
        softphoneFailure,
        clearSoftphoneFailure,
        micPermission,
        networkConnection,
        browserOnline,
        networkReachable,
        networkDropNotice,
        dismissNetworkDropNotice,
    } = useAWSConnect()

    const {
        callData,
        showModal: showIncomingModal,
        showCallAcceptedByOtherPopup,
        clearCallAcceptedByOther,
        isAcceptedAndConnecting, tokenPromptActive,
        hasPendingPreCallQuestions,
        showPendingPreCallGuardrail,
        softphoneRecoveryLocked,
        showSoftphoneRecoveryPanel,
        clearSoftphoneRecoveryLock,
        armSoftphoneRecoveryLock,
    } = useIncomingCall()

    const [fetchSoftphoneProbeConfig] = useLazyGetSoftphoneProbeConfigQuery()
    const [probePhase, setProbePhase] = useState<SoftphoneProbePhase>('idle')
    const [probeError, setProbeError] = useState<string | null>(null)
    const lastSoftphoneErrorTypeRef = useRef<string | null>(null)
    const probePhaseRef = useRef<SoftphoneProbePhase>('idle')
    const probeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const probeOutcomeRef = useRef<'success' | 'failure' | null>(null)
    const probeCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const probeDestroyWatchRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const settleClosedProbeRef = useRef<() => void>(() => {})
    const [probeAcwSuppressed, setProbeAcwSuppressed] = useState(false)

    useEffect(() => {
        if (softphoneFailure?.errorType) {
            lastSoftphoneErrorTypeRef.current = softphoneFailure.errorType
        }
    }, [softphoneFailure?.errorType])
    const finishProbeAttemptRef = useRef<(opts: {
        success: boolean
        error?: string | null
        endContact?: boolean
    }) => void>(() => {})
    useEffect(() => {
        probePhaseRef.current = probePhase
    }, [probePhase])

    // Persist callData (from Firebase/useIncomingCall) into activeCallData (ConnectStreamsContext)
    // as soon as it arrives. This ensures data survives the accept/clear cycle.
    const { setActiveCallData: setCtxCallData } = useAWSConnect()

    // Download the disconnect animation while the app is still online.  The
    // disconnected panel is rendered only after connectivity is lost, so using
    // its URL directly at that point is too late.  A blob URL also keeps the
    // already-downloaded asset usable for the lifetime of this page.
    useEffect(() => {
        const controller = new AbortController()
        let objectUrl: string | null = null

        void fetch(NO_INTERNET_VIDEO_URL, {
            cache: 'force-cache',
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Unable to preload disconnect animation (${response.status})`)
                return response.blob()
            })
            .then((blob) => {
                objectUrl = URL.createObjectURL(blob)
                setNetworkStatusVideoSrc(objectUrl)
            })
            .catch((error: unknown) => {
                // The static icon rendered below is intentional fallback for a
                // first load that begins while the browser is already offline.
                if ((error as DOMException)?.name !== 'AbortError') {
                    console.warn('Disconnect animation could not be preloaded', error)
                }
            })

        return () => {
            controller.abort()
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [])

    useEffect(() => {
        if (callData?.caller && !activeCallData) {
            console.log('===== WIDGET: Persisting callData to activeCallData', callData)
            const persisted = {
                callId: callData.callId,
                callHeaderId: callData.callHeaderId,
                caller: callData.caller,
                language: callData.language,
                channel: callData.channel,
                type: callData.type,
                timestamp: callData.timestamp,
                action: callData.action,
            }
            setCtxCallData(persisted)
            streamsStore.syncIncomingCallMeta({
                ...persisted,
                contactId: callData.contactId || callData.callId,
            })
        }
    }, [callData, activeCallData, setCtxCallData])

    // Sync voice enhancement from CCP when opening settings (agent profile may have changed).
    useEffect(() => {
        if (!isSettingsOpen) return
        streamsStore.syncVoiceEnhancementFromAgent()
    }, [isSettingsOpen])

    // Softphone failure notice (no auto-recovery)
    useEffect(() => {
        if (isLoggedIn) {
            setIsLoggingIn(false)
        }
    }, [isLoggedIn])

    const isACW = (agentStatus === 'AfterCallWork');
    // navigator.onLine can stay true through a real outage on some devices, so the
    // probe-backed networkReachable flag also drives the "Re-connecting" view.
    const isNetworkDown = !browserOnline || !networkReachable;
    const appUserEmail = currentUser?.result?.email?.trim() || '';
    const ccpUserEmail = agentEmail?.trim() || '';
    const hasCcpEmailMismatch = isLoggedIn && hasConnectEmailMismatch(appUserEmail, ccpUserEmail);
    const isMicBlocked = isMicrophoneUnavailable(micPermission);
    const softphoneProbeInFlight =
        probePhase === 'starting' || probePhase === 'waiting' || probePhase === 'closing'
    const availabilityBlockReason = softphoneRecoveryLocked && !softphoneProbeInFlight
        ? 'softphone'
        : hasPendingPreCallQuestions
            ? 'precall'
            : isMicBlocked
                ? 'microphone'
                : null;

    // Auto-dismiss network-drop notice after 10s.
    useEffect(() => {
        if (!networkDropNotice) return
        const timer = setTimeout(() => {
            dismissNetworkDropNotice()
        }, NETWORK_DROP_NOTICE_MS)
        return () => clearTimeout(timer)
    }, [networkDropNotice, dismissNetworkDropNotice])

    // Sign out of the CCP; streamsBootstrap re-inits the hidden iframe (no page reload).
    const handleCcpSignOut = async () => {
        if (hasPendingPreCallQuestions) {
            showPendingPreCallGuardrail()
            toaster('error', 'Complete pre-call questions before signing out.')
            return
        }
        try {
            await logout()
        } catch (e) {
            console.error('[ConnectWidget] CCP sign out failed:', e)
        }
    }

    const handleMismatchSignOut = async () => {
        setIsMismatchSigningOut(true)
        try {
            await logout()
        } catch (e) {
            console.error('[ConnectWidget] Mismatch sign out failed:', e)
        } finally {
            setIsMismatchSigningOut(false)
        }
    }

    const handleConfirmEndCall = async () => {
        setIsEndingCall(true)
        try {
            const rawCallHeaderId = activeCallData?.callHeaderId
            const code = parseCallHeaderCode(rawCallHeaderId)

            if (code != null) {
                await postSetCallEndedByInterpreter(code)
                callLogger.info('Successfully set CallEndedBy=Interpreter', {
                    code,
                    callHeaderId: rawCallHeaderId,
                    agentEmail,
                })
            }
        } catch (e) {
            callLogger.error('Failed to set CallEndedBy=Interpreter', {
                callHeaderId: activeCallData?.callHeaderId,
                error: String(e),
                agentEmail,
            })
            console.error('Failed to set CallEndedBy=Interpreter:', e)
        } finally {
            setIsEndCallConfirmOpen(false)
            setIsEndingCall(false)
            endCall()
        }
    }

    // ACW: 30s countdown then auto-close; clear interval when leaving ACW.
    // A softphone probe clears the contact immediately and never shows Call Ended.
    useEffect(() => {
        if (!isACW) {
            if (acwIntervalRef.current) {
                clearInterval(acwIntervalRef.current)
                acwIntervalRef.current = null
            }
            setAcwSecondsRemaining(null)
            return
        }
        if (probePhaseRef.current === 'closing' || probeAcwSuppressed) {
            if (probePhaseRef.current === 'closing') {
                clearContact()
            }
            return
        }
        setAcwSecondsRemaining(ACW_AUTO_CLOSE_SECONDS)
        acwIntervalRef.current = setInterval(() => {
            setAcwSecondsRemaining((prev) => {
                if (prev === null) return 0
                const next = prev - 1
                if (next <= 0) {
                    if (acwIntervalRef.current) {
                        clearInterval(acwIntervalRef.current)
                        acwIntervalRef.current = null
                    }
                    clearContact()
                    return 0
                }
                return next
            })
        }, 1000)
        return () => {
            if (acwIntervalRef.current) {
                clearInterval(acwIntervalRef.current)
                acwIntervalRef.current = null
            }
        }
    }, [isACW, clearContact, probeAcwSuppressed])

    // Keep Call Ended hidden until the probe agent has left After Call Work.
    useEffect(() => {
        if (softphoneProbeInFlight) return
        if (agentStatus !== 'AfterCallWork') {
            setProbeAcwSuppressed(false)
        }
    }, [agentStatus, softphoneProbeInFlight])

    const {
        addParticipant,
        refreshParticipants,
        conferenceParticipants,
        isAddingParticipant,
        sendDtmf,
        disconnectParticipant,
        holdParticipant,
        resumeParticipant,
        dialableCountries,
    } = useAWSConnect()

    // Filter countries based on agent configuration
    const filteredCountries = useMemo(() => {
        if (!dialableCountries || !Array.isArray(dialableCountries) || dialableCountries.length === 0) return defaultCountries;
        return defaultCountries.filter((country) => {
            const parsed = parseCountry(country);
            return dialableCountries.includes(parsed.iso2);
        });
    }, [dialableCountries]);

    // Refresh participants when connected
    useEffect(() => {
        if (isConnected) {
            refreshParticipants()
        }
    }, [isConnected, refreshParticipants])

    // Reset UI states when call ends
    useEffect(() => {
        if (!isConnected && !isACW) {
            setActiveView('none')
            setIsCallInfoOpen(false)
            setDtmfInput('')
            setConferenceNumber('')
            setConferenceError(null)
            setIsVideoOpen(false)
            setIsEndCallConfirmOpen(false)
            setIsEndingCall(false)
        }
    }, [isConnected, isACW])

    const focusCallPanel = React.useCallback((focusTarget: 'panel' | 'primary-control' = 'panel') => {
        setToggleExpand(true)
        window.setTimeout(() => {
            if (focusTarget === 'primary-control' && muteButtonRef.current) {
                muteButtonRef.current.focus()
                return
            }
            panelRef.current?.focus({ preventScroll: true })
        }, 120)
    }, [])

    useEffect(() => {
        const onFocusRequest = () => focusCallPanel('panel')
        window.addEventListener(CCP_FOCUS_REQUEST_EVENT, onFocusRequest)
        return () => window.removeEventListener(CCP_FOCUS_REQUEST_EVENT, onFocusRequest)
    }, [focusCallPanel])

    useEffect(() => {
        if (!isEndCallConfirmOpen || isEndingCall) return
        const focusId = window.setTimeout(() => endCallConfirmActionRef.current?.focus(), 50)
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsEndCallConfirmOpen(false)
                return
            }
            if (event.key === 'Enter' && !isTypingTarget(event.target)) {
                event.preventDefault()
                void handleConfirmEndCall()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.clearTimeout(focusId)
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [isEndCallConfirmOpen, isEndingCall])

    // Auto-open video panel when a video-capable call connects and credentials are ready
    useEffect(() => {
        if (softphoneProbeInFlight) return
        if (videoCapabilities?.hasVideoRTC && videoConnectionInfo) {
            setIsVideoOpen(true)
        }
    }, [videoCapabilities, videoConnectionInfo, softphoneProbeInFlight])

    // Auto-expand widget on incoming call or connection (master tab only)
    useEffect(() => {
        if (isMasterTab !== true) return;
        if (
            isConnected ||
            isIncomingCall ||
            showCallAcceptedByOtherPopup ||
            isAcceptedAndConnecting ||
            isConnectionFailed ||
            networkDropNotice ||
            showSoftphoneRecoveryPanel ||
            softphoneProbeInFlight
        ) {
            setToggleExpand(true)
        }
    }, [
        isMasterTab,
        isConnected,
        isIncomingCall,
        showCallAcceptedByOtherPopup,
        isAcceptedAndConnecting,
        isConnectionFailed,
        networkDropNotice,
        showSoftphoneRecoveryPanel,
        softphoneProbeInFlight,
    ])

    // Publish call-active state for observer tabs
    useEffect(() => {
        if (isMasterTab !== true) return;
        publishCallActive(
            !softphoneProbeInFlight &&
            (isConnected || isIncomingCall || isAcceptedAndConnecting || isConnectionFailed),
        );
    }, [isMasterTab, isConnected, isIncomingCall, isAcceptedAndConnecting, isConnectionFailed, publishCallActive, softphoneProbeInFlight])

    // Publish agent status for observer tabs
    useEffect(() => {
        if (isMasterTab !== true) return;
        publishAgentState({ isLoggedIn, agentStatus });
    }, [isMasterTab, isLoggedIn, agentStatus, publishAgentState])

    useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (isConnected) {
            e.preventDefault();
            e.returnValue = ''; // Legacy Chrome support
        }
         // No return value needed — modern browsers ignore it anyway
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
}, [isConnected]);

    useEffect(() => {
        if (isLoggedIn) {
            setIsLoggingIn(false)
        }
    }, [isLoggedIn])

    // When the "Force Update Token" modal appears, the interpreter can't receive calls — collapse
    // the widget and force the agent offline once, on the transition. While it stays active the
    // controls below are disabled so they can't go back Available until the token is fixed.
    const prevTokenPromptRef = useRef(false)
    useEffect(() => {
        if (tokenPromptActive && !prevTokenPromptRef.current) {
            setToggleExpand(false)
            if (isLoggedIn) {
                setAgentOffline()
            }
        }
        prevTokenPromptRef.current = tokenPromptActive
    }, [tokenPromptActive, isLoggedIn, setAgentOffline])

    // Status Toggle Handler
    const handleStatusToggle = (checked: boolean) => {
        if (checked && softphoneRecoveryLocked && !softphoneProbeInFlight) {
            setAgentOffline()
            toaster('error', 'Complete the softphone connection test before becoming available.')
            return
        }
        if (checked && hasPendingPreCallQuestions) {
            showPendingPreCallGuardrail()
            setAgentOffline()
            toaster('error', 'Complete pre-call questions before becoming available.')
            return
        }
        if (checked && isMicBlocked) {
            setAgentOffline()
            toaster('error', 'Allow microphone access before becoming available.')
            return
        }
        if (checked) {
            setAgentAvailable()
        } else {
            setAgentOffline()
        }
    }

    const clearProbeTimeout = () => {
        if (probeTimeoutRef.current) {
            clearTimeout(probeTimeoutRef.current)
            probeTimeoutRef.current = null
        }
    }

    const clearProbeTeardownTimers = () => {
        if (probeCloseTimerRef.current) {
            clearTimeout(probeCloseTimerRef.current)
            probeCloseTimerRef.current = null
        }
        if (probeDestroyWatchRef.current) {
            clearTimeout(probeDestroyWatchRef.current)
            probeDestroyWatchRef.current = null
        }
    }

    const probeContactStillOpen = () => {
        const snap = streamsStore.getSnapshot()
        return (
            !!snap.contactId ||
            snap.callPhase === 'ringing' ||
            snap.callPhase === 'connecting' ||
            snap.callPhase === 'connected' ||
            snap.callPhase === 'acw'
        )
    }

    const settleClosedProbe = () => {
        if (probePhaseRef.current !== 'closing') return
        if (probeContactStillOpen()) return
        const outcome = probeOutcomeRef.current
        probeOutcomeRef.current = null
        clearProbeTeardownTimers()
        streamsStore.clearSoftphoneProbeInFlight()
        if (outcome === 'success') {
            // Unlock before Available so the offline watchdog does not fight it.
            clearSoftphoneRecoveryLock()
            probePhaseRef.current = 'idle'
            setProbePhase('idle')
            setProbeError(null)
            window.setTimeout(() => setAgentAvailable(), 800)
            toaster('success', 'Softphone test connected. You are now Available.')
            return
        }
        probePhaseRef.current = 'failed'
        setProbePhase('failed')
        window.setTimeout(() => setAgentOffline(), 800)
    }
    settleClosedProbeRef.current = settleClosedProbe

    const finishProbeAttempt = (opts: {
        success: boolean
        error?: string | null
        endContact?: boolean
    }) => {
        const phase = probePhaseRef.current
        const storeInFlight = streamsStore.getSnapshot().softphoneProbeInFlight
        // Ignore late events after the attempt already settled or is tearing down.
        if (phase === 'closing') return
        if (phase !== 'starting' && phase !== 'waiting' && !storeInFlight) {
            return
        }
        clearProbeTimeout()
        startProbeCooldown()

        if (opts.endContact === false) {
            streamsStore.clearSoftphoneProbeInFlight()
            probeOutcomeRef.current = null
            probePhaseRef.current = 'failed'
            setProbePhase('failed')
            setProbeError(
                opts.error || 'Test connection did not complete. Try again after the cooldown.',
            )
            return
        }

        // Stay on the test panel until the contact is hung up and destroyed.
        probeOutcomeRef.current = opts.success ? 'success' : 'failure'
        if (!opts.success) {
            setProbeError(
                opts.error || 'Test connection did not complete. Try again after the cooldown.',
            )
        }
        probePhaseRef.current = 'closing'
        setProbePhase('closing')
        setProbeAcwSuppressed(true)

        clearProbeTeardownTimers()
        probeCloseTimerRef.current = setTimeout(() => {
            probeCloseTimerRef.current = null
            try {
                streamsStore.endCurrent()
            } catch {
                /* ignore */
            }
            if (!probeContactStillOpen()) {
                settleClosedProbeRef.current()
            }
        }, opts.success ? 1500 : 0)
        probeDestroyWatchRef.current = setTimeout(() => {
            probeDestroyWatchRef.current = null
            if (probePhaseRef.current !== 'closing') return
            try {
                streamsStore.clearCurrent()
            } catch {
                /* ignore */
            }
            settleClosedProbeRef.current()
        }, opts.success ? 20_000 : 15_000)
    }
    finishProbeAttemptRef.current = finishProbeAttempt

    const resolveProbeNumber = async (): Promise<string> => {
        const result = await fetchSoftphoneProbeConfig().unwrap()
        const number = String(
            (result as any)?.destinationPhoneNumber ||
                (result as any)?.DestinationPhoneNumber ||
                '',
        ).replace(/\s+/g, '')
        if (!number) {
            throw new Error('Softphone probe number is not configured')
        }
        return number
    }

    const handleTestConnection = async () => {
        if (softphoneProbeInFlight) return
        // Agent init, login, and session restore must finish before a probe dial.
        if (!isInitialized || !isLoggedIn) return
        setProbeAcwSuppressed(true)
        setProbeError(null)
        setProbePhase('starting')
        try {
            const probeNumber = await resolveProbeNumber()
            // Keep Offline — agent.connect works without Available.
            await streamsStore.startSoftphoneProbeOutbound(probeNumber)
            setProbePhase('waiting')
            clearProbeTimeout()
            probeTimeoutRef.current = setTimeout(() => {
                finishProbeAttemptRef.current({
                    success: false,
                    error: 'Test call timed out before softphone connected.',
                })
            }, 90_000)
        } catch (err: any) {
            const message =
                err?.data?.text ||
                err?.data?.Text ||
                err?.error ||
                err?.message ||
                'Could not start the softphone test call.'
            finishProbeAttempt({ success: false, error: String(message), endContact: false })
        }
    }

    // Accepted, then the softphone failed. Remember it, but keep Test Connection
    // hidden until this contact is closed and destroyed.
    const holdRecoveryUntilContactClosedRef = useRef(false)
    const [holdRecoveryUntilContactClosed, setHoldRecoveryUntilContactClosed] = useState(false)
    useEffect(() => {
        if (softphoneProbeInFlight || !isConnectionFailed) return
        if (!isAcceptedAndConnecting && !showSoftphoneRecoveryPanel) return
        if (holdRecoveryUntilContactClosedRef.current) return
        holdRecoveryUntilContactClosedRef.current = true
        setHoldRecoveryUntilContactClosed(true)
        if (isAcceptedAndConnecting) armSoftphoneRecoveryLock()
    }, [
        isConnectionFailed,
        isAcceptedAndConnecting,
        showSoftphoneRecoveryPanel,
        softphoneProbeInFlight,
        armSoftphoneRecoveryLock,
    ])
    const releaseRecoveryAfterContactClosed = () => {
        if (!holdRecoveryUntilContactClosedRef.current) return
        holdRecoveryUntilContactClosedRef.current = false
        setHoldRecoveryUntilContactClosed(false)
    }

    // CCP detached after softphone failure → offline + arm recovery lock.
    // Outbound probe CONNECTED → hang up, then unlock once the contact is destroyed.
    useEffect(() => {
        return streamsStore.onEvent((event) => {
            if (event.type === 'contact:detached' && event.hadSoftphoneFailure) {
                armSoftphoneRecoveryLock()
                setAgentOffline()
            }
            if (
                event.type === 'softphoneProbe:connected' ||
                (event.type === 'contact:connected' &&
                    streamsStore.getSnapshot().softphoneProbeInFlight)
            ) {
                finishProbeAttemptRef.current({ success: true })
            }
            if (
                event.type === 'softphone:failure' &&
                (streamsStore.getSnapshot().softphoneProbeInFlight ||
                    probePhaseRef.current === 'waiting' ||
                    probePhaseRef.current === 'starting')
            ) {
                finishProbeAttemptRef.current({
                    success: false,
                    error: event.failure?.summary || 'Softphone media failed during the test call.',
                })
            }
            if (event.type === 'contact:ended' && probePhaseRef.current === 'closing') {
                streamsStore.clearCurrent()
            }
            if (event.type === 'contact:destroyed') {
                releaseRecoveryAfterContactClosed()
                settleClosedProbeRef.current()
            }
        })
    }, [armSoftphoneRecoveryLock, setAgentOffline])

    useEffect(() => {
        return () => {
            clearProbeTimeout()
            clearProbeTeardownTimers()
        }
    }, [])

    // Keep CCP offline while mandatory pre-call answers remain outstanding.
    // Skip while a softphone failure is active — Connect owns agent state during media teardown.
    useEffect(() => {
        if (!hasPendingPreCallQuestions) return
        if (softphoneFailure) return
        if (agentStatus === 'Available' || agentStatus === 'Routable') {
            setAgentOffline()
        }
    }, [hasPendingPreCallQuestions, agentStatus, setAgentOffline, softphoneFailure])

    // Keep CCP offline while softphone recovery lock is active (except during outbound probe).
    // Also skip Busy/Calling/PendingBusy — putAgentState(Offline) fails on an active contact.
    useEffect(() => {
        if (!isLoggedIn || !softphoneRecoveryLocked || softphoneProbeInFlight) return
        if (streamsStore.getSnapshot().softphoneProbeInFlight) return
        if (isConnected || isIncomingCall || isConnectionFailed) return
        if (agentStatus === 'Available' || agentStatus === 'Routable') {
            setAgentOffline()
        }
    }, [
        isLoggedIn,
        softphoneRecoveryLocked,
        softphoneProbeInFlight,
        agentStatus,
        setAgentOffline,
        isConnected,
        isIncomingCall,
        isConnectionFailed,
    ])

    // Keep CCP offline while the microphone is blocked or unavailable.
    useEffect(() => {
        if (!isLoggedIn || !isMicBlocked) return
        if (agentStatus === 'Available' || agentStatus === 'Routable') {
            setAgentOffline()
        }
    }, [isLoggedIn, isMicBlocked, agentStatus, setAgentOffline])

    // Block availability when CCP is signed in as a different user than Adastra Connect.
    useEffect(() => {
        if (!hasCcpEmailMismatch) return
        if (agentStatus === 'Available' || agentStatus === 'Routable') {
            setAgentOffline()
        }
    }, [hasCcpEmailMismatch, agentStatus, setAgentOffline])

    // Get status indicator color
    const getStatusColor = () => {
        if (!isLoggedIn) return '#A1A5B7' // Gray - offline
        if (isConnected || isCallStatus(agentStatus)) return '#D7383C' // Red - busy
        if (agentStatus === 'Available') return '#60BB1A' // Green - available
        return '#A1A5B7' // Gray - offline
    }

    // Keypad Logic
    const playKeypadSound = useRef(() => {
        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
            const osc = ctx.createOscillator()
            const gain = ctx.createGain()
            osc.connect(gain)
            gain.connect(ctx.destination)
            osc.frequency.value = 800
            osc.type = 'sine'
            gain.gain.setValueAtTime(0.15, ctx.currentTime)
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.06)
            osc.start(ctx.currentTime)
            osc.stop(ctx.currentTime + 0.06)
        } catch (_) {}
    }).current

    const handleKeypadPress = (digit: string) => {
        playKeypadSound()
        setDtmfInput(prev => prev + digit)
        sendDtmf(digit)
    }

    const handleKeypadBackspace = () => {
        setDtmfInput(prev => '')
    }

    // Keyboard input for keypad view (DTMF)
    useEffect(() => {
        if (activeView !== 'keypad') return
        const keyToDigit: Record<string, string> = {
            '0': '0', '1': '1', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9',
            '*': '*', '#': '#', '+': '+',
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Backspace') {
                e.preventDefault()
                handleKeypadBackspace()
                return
            }
            const digit = keyToDigit[e.key]
            if (digit && !e.ctrlKey && !e.metaKey && !e.altKey) {
                if (isTypingTarget(e.target)) return
                e.preventDefault()
                playKeypadSound()
                setDtmfInput(prev => prev + digit)
                sendDtmf(digit)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [activeView, sendDtmf])

    // Conference Logic
    const handleAddConferenceParticipant = async () => {
        if (canTransferContact === false) {
            setConferenceError('This contact cannot have participants added right now.')
            return
        }

        // Guard before dialing: Connect answers a malformed endpoint with a
        // BadEndpointException carrying its whole stack trace.
        const guard = validateDialNumber(conferenceNumber, dialableCountries)
        if (!guard.ok) {
            setConferenceError(guard.message)
            return
        }

        setConferenceError(null)
        try {
            await addParticipant(guard.e164)
            setConferenceNumber('')
            setActiveView('none') // Switch back to main view to see list
        } catch (error) {
            console.error('Failed to add conference participant:', error)
            const message = friendlyDialError(error)
            setConferenceError(message)
            toaster('error', message)
        }
    }

    const handleConferenceKeypadPress = (digit: string) => {
        playKeypadSound()
        setConferenceNumber(prev => prev + digit)
    }

    const handleConferenceBackspace = () => {
        setConferenceNumber(prev => prev.slice(0, -1))
        setConferenceError(null)
    }

    const handleLoginClick = () => {
        setIsLoggingIn(true)
        openLoginPopup(() => {
            console.log('[ConnectWidget] Login popup closed');
            setIsLoggingIn(false);
        })
    }

    const handleAuthRecovery = () => {
        if (
            ccpAuthState === 'auth_retries_exhausted' ||
            ccpAuthState === 'cti_auth_retries_exhausted'
        ) {
            window.location.reload()
            return
        }
        setIsLoggingIn(true)
        try {
            reauthenticate()
        } catch {
            handleLoginClick()
        }
    }

    const isAuthorizing = isLoggingIn || ccpAuthState === 'authorizing'

    // CCP boot phase — drives both the idle shell and the progress ring around the pill.
    const isSessionProbePending = !isLoggedIn && (
        ccpAuthState === 'ccp_ready' || ccpAuthState === 'authorized'
    )
    const bootPhase = getConnectIdleAuthPhase({
        isLoggedIn,
        isInitialized,
        ccpAuthState,
        isSessionProbePending,
        isAuthorizing,
    })
    const pillRing = usePillRingState({ isMasterTab, bootPhase })

    // Stable callbacks passed to KeypadButton (defined outside this component)
    const handleKeypadPressStable = React.useCallback((digit: string) => {
        playKeypadSound()
        setDtmfInput(prev => prev + digit)
        sendDtmf(digit)
    }, [sendDtmf])

    const handleConferenceKeypadPressStable = React.useCallback((digit: string) => {
        playKeypadSound()
        setConferenceNumber(prev => prev + digit)
        setConferenceError(null)
    }, [])

    // Parse caller info - try activeCallData first, then callData (from useIncomingCall), then contact.attributes as fallback
    const callerInfo = React.useMemo(() => {
        // Try to parse from the best available source
        const callerValue = activeCallData?.caller || callData?.caller
        if (callerValue) {
            try {
                // Handle both string and object cases
                let parsed: any;
                if (typeof callerValue === 'string') {
                    parsed = JSON.parse(callerValue)
                } else if (typeof callerValue === 'object' && callerValue !== null) {
                    parsed = callerValue
                } else {
                    return {
                        requester: String(callerValue),
                        company: 'Unknown Company'
                    }
                }

                // Ensure parsed is an object with expected properties
                if (parsed && typeof parsed === 'object') {
                    const firstName = parsed.firstName ? String(parsed.firstName) : ''
                    const lastName = parsed.lastName ? String(parsed.lastName) : ''
                    const companyName = parsed.companyName ? String(parsed.companyName) : ''

                    return {
                        requester: `${firstName} ${lastName}`.trim() || 'Unknown Caller',
                        company: companyName || 'Unknown Company'
                    }
                }
            } catch (error) {
                console.warn('[ConnectWidget] Error parsing caller info:', error)
                // Fallback: try to convert to string safely
                return {
                    requester: typeof callerValue === 'string' ? callerValue : 'Unknown Caller',
                    company: 'Unknown Company'
                }
            }
        }

        // Fallback: extract from CCP contact attributes (meeting pin calls)
        const attrs = contact?.attributes
        if (attrs) {
            const firstName = attrs.requesterFirstName ? String(attrs.requesterFirstName) : ''
            const lastName = attrs.requesterLastName ? String(attrs.requesterLastName) : ''
            const fullName = attrs.requesterFullName ? String(attrs.requesterFullName) : `${firstName} ${lastName}`.trim()
            const company = attrs.customerName ? String(attrs.customerName) : ''

            if (fullName || company) {
                return {
                    requester: fullName || 'Unknown Caller',
                    company: company || 'Unknown Company'
                }
            }
        }

        return {
            requester: contact?.callerDisplayName ? String(contact.callerDisplayName) : 'Unknown Caller',
            company: 'Unknown Company'
        }
    }, [activeCallData, callData, contact])

    // Incoming CCP ring: announce + focus Accept (modal owns a11y when its popup is open)
    useEffect(() => {
        if (softphoneProbeInFlight) return
        if (!isIncomingCall || showIncomingModal) {
            if (!isIncomingCall) setLiveA11yAnnouncement('')
            return
        }
        setIsSettingsOpen(false)
        setToggleExpand(true)
        setLiveA11yAnnouncement(
            buildIncomingCallAnnouncement({
                company: callerInfo.company,
                requester: callerInfo.requester,
            }),
        )
        const id = window.setTimeout(() => {
            acceptCallButtonRef.current?.focus()
        }, 100)
        return () => window.clearTimeout(id)
    }, [isIncomingCall, showIncomingModal, callerInfo.company, callerInfo.requester, softphoneProbeInFlight])

    useEffect(() => {
        if (softphoneProbeInFlight) return
        if (isAcceptedAndConnecting && !isConnected) {
            wasConnectingRef.current = true
            focusCallPanel('panel')
            setLiveA11yAnnouncement(buildConnectingCallAnnouncement())
            return
        }
        if (isConnected && contact && wasConnectingRef.current) {
            wasConnectingRef.current = false
            focusCallPanel('primary-control')
            setLiveA11yAnnouncement(
                buildCallConnectedAnnouncement({
                    company: callerInfo.company,
                    requester: callerInfo.requester,
                }),
            )
            return
        }
        if (!isAcceptedAndConnecting && !isConnected) {
            wasConnectingRef.current = false
        }
    }, [
        isAcceptedAndConnecting,
        isConnected,
        contact,
        callerInfo.company,
        callerInfo.requester,
        focusCallPanel,
        softphoneProbeInFlight,
    ])

    // Active-call keyboard shortcuts
    useEffect(() => {
        if (softphoneProbeInFlight) return
        if (!isConnected || !contact || !toggleExpand || showIncomingModal || isEndCallConfirmOpen || isSettingsOpen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (isTypingTarget(event.target)) return

            if (isMuteCallShortcut(event)) {
                event.preventDefault()
                toggleAgentMute()
                setLiveA11yAnnouncement(buildMuteToggleAnnouncement(!isAgentMuted))
                return
            }
            if (isHoldCallShortcut(event)) {
                event.preventDefault()
                toggleCallHold()
                setLiveA11yAnnouncement(buildHoldToggleAnnouncement(!isCallOnHold))
                return
            }
            if (isConferenceCallShortcut(event)) {
                event.preventDefault()
                setActiveView((view) => (view === 'conference' ? 'none' : 'conference'))
                return
            }
            if (isKeypadCallShortcut(event)) {
                event.preventDefault()
                setActiveView((view) => (view === 'keypad' ? 'none' : 'keypad'))
                return
            }
            if (isCallInfoShortcut(event)) {
                event.preventDefault()
                setIsCallInfoOpen((open) => !open)
                return
            }
            if (isEndCallShortcut(event)) {
                event.preventDefault()
                setIsEndCallConfirmOpen(true)
            }
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [
        isConnected,
        contact,
        toggleExpand,
        showIncomingModal,
        isEndCallConfirmOpen,
        isSettingsOpen,
        isAgentMuted,
        isCallOnHold,
        toggleAgentMute,
        toggleCallHold,
        softphoneProbeInFlight,
    ])

    // Keyboard shortcuts for CCP incoming screen (not when Firebase modal is showing)
    useEffect(() => {
        if (softphoneProbeInFlight || !isIncomingCall || showIncomingModal) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (isAcceptCallShortcut(event)) {
                event.preventDefault()
                acceptCall()
                return
            }
            if (isDeclineCallShortcut(event)) {
                event.preventDefault()
                rejectCall()
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [isIncomingCall, showIncomingModal, acceptCall, rejectCall, softphoneProbeInFlight])

    // Render Active Call Layout
    const renderActiveCall = () => {
        const callerSummary = `${callerInfo.company}, ${callerInfo.requester}`
        return (
            <div
                className="cw-card-content cw-fade-in-up h-100 d-flex flex-column"
                role="region"
                aria-label={`Active call with ${callerSummary}`}
            >
                {/* 1. Header Section (Fixed Top) */}
                <div
                    className="d-flex justify-content-between align-items-center p-4 border-bottom bg-white"
                    style={{ flexShrink: 0 }}
                    role="group"
                    aria-label={`Caller information for ${callerSummary}`}
                >
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, overflow: 'hidden' }}>
                        <div className="cw-avatar-large text-white flex-shrink-0" style={{ background: '#374151' }}>
                            {(callerInfo.company || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="cw-selectable" style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                            <ClampWithTooltip text={callerInfo.company} className="fw-bold fs-5 text-dark" />
                            <ClampWithTooltip text={callerInfo.requester} className="text-muted fs-7" />
                        </div>
                    </div>
                    <div
                        className="d-flex align-items-center gap-2 px-3 py-1 font-monospace"
                        style={{ border: '1px solid rgb(194, 194, 194)', borderRadius: '8px' }}
                        role="timer"
                        aria-label={`Call duration ${formatDuration(callDuration)}`}
                        aria-live="off"
                    >
                        <div className="cw-recording-dot" aria-hidden="true"></div>
                        <div className="fw-semibold text-gray-800" style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }} aria-hidden="true">
                            {formatDuration(callDuration)}
                        </div>
                    </div>
                </div>

                {/* 2. Dynamic Middle Section (Scrollable) */}
                <div className="flex-grow-1 overflow-hidden position-relative ">
                    <div className="h-100 overflow-auto p-3">

                        {isNetworkDown ? (
                            <div
                                className="cw-network-status h-100 d-flex flex-column align-items-center justify-content-center text-center px-3"
                                role="alert"
                                aria-live="assertive"
                                aria-labelledby="cw-network-status-message"
                            >
                                {networkStatusVideoSrc ? (
                                    <video
                                        className="cw-network-status-video"
                                        src={networkStatusVideoSrc}
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        aria-hidden="true"
                                        onError={() => setNetworkStatusVideoSrc(null)}
                                    />
                                ) : (
                                    <WifiOff
                                        className="cw-network-status-fallback"
                                        aria-hidden="true"
                                    />
                                )}
                                <p id="cw-network-status-message" className="cw-network-status-message mb-0">
                                    {networkConnection.status === 'failed'
                                        ? 'Call Disconnected'
                                        : 'Re-connecting... Please connect to a stable internet connection'}
                                </p>
                            </div>
                        ) : (
                        <>
                        {/* Middle Views */}
                        {activeView === 'keypad' && (
                            <div className="cw-keypad-container border-0">
                                <div className='d-flex justify-content-between align-items-center position-relative'>
                                    <span style={{ width: '20px' }}></span>

                                    <div className="cw-keypad-display mb-2 border-0">
                                        <span className="fs-2 fw-bold text-dark">{dtmfInput}</span>
                                    </div>

                                    {dtmfInput && (
                                        <button
                                            type="button"
                                            className="cw-backspace-btn"
                                            aria-label="Delete last digit"
                                            onClick={handleKeypadBackspace}
                                        >
                                            <CircleX size={14} aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                                <div className="cw-keypad-grid">
                                    <div className="d-flex justify-content-center gap-3 mb-3">
                                        <KeypadButton digit="1" onPress={handleKeypadPressStable} />
                                        <KeypadButton digit="2" onPress={handleKeypadPressStable} />
                                        <KeypadButton digit="3" onPress={handleKeypadPressStable} />
                                    </div>
                                    <div className="d-flex justify-content-center gap-2 mb-2">
                                        <KeypadButton digit="4" onPress={handleKeypadPressStable} />
                                        <KeypadButton digit="5" onPress={handleKeypadPressStable} />
                                        <KeypadButton digit="6" onPress={handleKeypadPressStable} />
                                    </div>
                                    <div className="d-flex justify-content-center gap-2 mb-2">
                                        <KeypadButton digit="7" onPress={handleKeypadPressStable} />
                                        <KeypadButton digit="8" onPress={handleKeypadPressStable} />
                                        <KeypadButton digit="9" onPress={handleKeypadPressStable} />
                                    </div>
                                    <div className="d-flex justify-content-center gap-2 mb-2">
                                        <KeypadButton digit="*" onPress={handleKeypadPressStable} />
                                        <KeypadButton digit="0" onPress={handleKeypadPressStable} />
                                        <KeypadButton digit="#" onPress={handleKeypadPressStable} />
                                    </div>
                                    {/* <div className="d-flex justify-content-center gap-2 mb-2">
                                        <KeypadButton digit="+" onPress={handleKeypadPressStable} />
                                    </div> */}
                                </div>
                            </div>
                        )}

                        {activeView === 'conference' && (
                            <div className="cw-conference-container border-0 p-2">

                                {/* Add Participant Section */}
                                <div className="mb-4">
                                    <label className="fs-7 fw-bold text-muted mb-1 text-uppercase ls-1">Add Participant</label>
                                    <div
                                        className="d-flex mb-3 align-items-center"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                // Let the handler's validation report the problem inline
                                                // instead of silently swallowing the Enter press.
                                                if (!isAddingParticipant && conferenceNumber) {
                                                    handleAddConferenceParticipant()
                                                }
                                            }
                                        }}
                                    >
                                        <PhoneInput
                                            defaultCountry="us"
                                            value={conferenceNumber}
                                            countries={filteredCountries}
                                            onChange={(phone) => {
                                                setConferenceNumber(phone)
                                                setConferenceError(null)
                                            }}
                                            inputProps={{
                                                'aria-label': 'Participant phone number',
                                                'aria-invalid': !!conferenceError,
                                                'aria-describedby': conferenceError ? 'cw-conference-error' : undefined,
                                            }}
                                            countrySelectorStyleProps={{
                                                buttonStyle: { height: '36px', borderRadius: '12px 0 0 12px', paddingLeft: '10px', paddingRight: '6px' },
                                            }}
                                            inputStyle={{ width: '100%', height: '36px', fontSize: '13px' }}
                                            style={{ flex: 1 }}
                                        />
                                        {/* Add Button */}
                                        <button
                                            type="button"
                                            className="btn text-white fw-bold px-4 d-flex justify-content-center align-items-center"
                                            style={{ borderRadius: '0 12px 12px 0', backgroundColor: '#3fb769ff', height: '36px' }}
                                            onClick={handleAddConferenceParticipant}
                                            disabled={
                                                isAddingParticipant ||
                                                canTransferContact === false ||
                                                !conferenceNumber ||
                                                conferenceNumber.replace(/\D/g, '').length < 4
                                            }
                                            aria-label={
                                                canTransferContact === false
                                                    ? 'Add participant unavailable for this contact'
                                                    : isAddingParticipant
                                                        ? 'Adding participant'
                                                        : 'Add participant'
                                            }
                                            title={
                                                canTransferContact === false
                                                    ? 'Transfer / add participant is not allowed for this contact'
                                                    : undefined
                                            }
                                        >
                                            {isAddingParticipant ? (
                                                <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                                            ) : (
                                                <Phone size={13} aria-hidden="true" />
                                            )}
                                        </button>
                                    </div>
                                    {canTransferContact === false ? (
                                        <div className="fs-8 text-danger mb-2">
                                            Adding participants is not available for this contact right now.
                                        </div>
                                    ) : conferenceError && (
                                        <div id="cw-conference-error" className="fs-8 text-danger mb-2" role="alert">
                                            {conferenceError}
                                        </div>
                                    )}

                                    {/* Conference Keypad */}
                                    <div className="position-relative cw-keypad-grid transform-scale-90">
                                        <div className="d-flex justify-content-center gap-2 mb-2">
                                            <KeypadButton digit="1" onPress={handleConferenceKeypadPressStable} />
                                            <KeypadButton digit="2" onPress={handleConferenceKeypadPressStable} />
                                            <KeypadButton digit="3" onPress={handleConferenceKeypadPressStable} />
                                            <div className="position-absolute top-0 end-0">
                                                {conferenceNumber && (
                                                    <button
                                                        type="button"
                                                        className="cw-backspace-btn"
                                                        aria-label="Delete last digit"
                                                        onClick={handleConferenceBackspace}
                                                        style={{ width: '42px', height: '42px' }}
                                                    >
                                                        <Delete size={17} aria-hidden="true" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-center gap-2 mb-2">
                                            <KeypadButton digit="4" onPress={handleConferenceKeypadPressStable} />
                                            <KeypadButton digit="5" onPress={handleConferenceKeypadPressStable} />
                                            <KeypadButton digit="6" onPress={handleConferenceKeypadPressStable} />
                                        </div>
                                        <div className="d-flex justify-content-center gap-2 mb-2">
                                            <KeypadButton digit="7" onPress={handleConferenceKeypadPressStable} />
                                            <KeypadButton digit="8" onPress={handleConferenceKeypadPressStable} />
                                            <KeypadButton digit="9" onPress={handleConferenceKeypadPressStable} />
                                        </div>
                                        <div className="d-flex justify-content-center gap-2 mb-2">
                                            <KeypadButton digit="*" onPress={handleConferenceKeypadPressStable} />
                                            <KeypadButton digit="0" onPress={handleConferenceKeypadPressStable} />
                                            <KeypadButton digit="#" onPress={handleConferenceKeypadPressStable} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Default Empty State / Spacer if nothing selected */}
                        {activeView === 'none' && (
                            <>
                                {conferenceParticipants.length > 2 ? (
                                    <div className="cw-participants-list-main p-3 h-100 overflow-auto">
                                        <div className='d-flex justify-content-between align-items-center mb-3'>
                                            <label className="fs-7 fw-bold text-muted text-uppercase ls-1 px-1">Active Participants</label>
                                        </div>

                                        {conferenceParticipants.slice(1).map((p, idx) => {
                                            // Determine state color and label
                                            const getStateInfo = () => {
                                                if (p.isOnHold) return { color: '#FFE897', label: 'On Hold' };
                                                if (p.isConnected) return { color: '#22C55E', label: formatDuration(Math.floor(p.stateDurationMs / 1000)) };
                                                return { color: '#9CA3AF', label: 'Disconnected' };
                                            };
                                            const stateInfo = getStateInfo();
                                            const isDisconnected = !p.isConnected && !p.isOnHold;

                                            // Card background based on state
                                            const cardBg = isDisconnected ? '#F3F4F6' : p.isOnHold ? '#FFFBEB' : '#F0FDF4';
                                            const cardBorder = isDisconnected ? '#888a8dff' : p.isOnHold ? '#d5ad34ff' : '#3fb769ff';

                                            const participantName = p.phoneNumber || callerInfo.company || 'Unknown Participant'
                                            const holdResumeLabel = p.isOnHold
                                                ? (p.canResume === false
                                                    ? `Resume not available yet for ${participantName}`
                                                    : `Resume ${participantName}`)
                                                : `Hold ${participantName}`

                                            return (
                                                <div key={p.connectionId || idx}
                                                    className="d-flex justify-content-between align-items-center p-3 rounded-3 mb-2"
                                                    style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, opacity: isDisconnected ? 0.6 : 1 }}>
                                                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                                        <div className="d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                                            style={{ width: 28, height: 28, color: cardBorder }}
                                                            aria-hidden="true"
                                                        >
                                                            <Phone />
                                                        </div>
                                                        <div className="cw-selectable" style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                                                            <ClampWithTooltip text={participantName} className="text-dark fs-6 fw-bold" />
                                                            <div className="d-flex align-items-center gap-1 fs-7" style={{ color: cardBorder }}>
                                                                <span className="cw-status-dot" style={{ backgroundColor: cardBorder }} aria-hidden="true"></span>
                                                                {stateInfo.label}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {!isDisconnected && (
                                                        <div className="d-flex align-items-center gap-1">
                                                            {/* Hold/Resume - for connected or on-hold participants */}
                                                            {(p.isConnected || p.isOnHold) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon rounded-circle"
                                                                    aria-label={holdResumeLabel}
                                                                    title={
                                                                        p.isOnHold
                                                                            ? (p.canResume === false
                                                                                ? 'Resume not available yet'
                                                                                : 'Resume Participant')
                                                                            : 'Hold Participant'
                                                                    }
                                                                    disabled={p.isOnHold && p.canResume === false}
                                                                    onClick={() => p.isOnHold ? resumeParticipant(p.connectionId) : holdParticipant(p.connectionId)}
                                                                    style={{
                                                                        width: '30px',
                                                                        height: '30px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        backgroundColor: p.isOnHold ? '#F59E0B' : '#9CA3AF',
                                                                        border: 'none',
                                                                        opacity: p.isOnHold && p.canResume === false ? 0.45 : 1,
                                                                    }}
                                                                >
                                                                    {p.isOnHold
                                                                        ? <Play size={14} color="white" aria-hidden="true" />
                                                                        : <Pause size={14} fill="white" color="white" aria-hidden="true" />}
                                                                </button>
                                                            )}
                                                            {/* Disconnect - always shown for non-disconnected */}
                                                            <button
                                                                type="button"
                                                                className="btn btn-icon btn-danger rounded-3"
                                                                aria-label={`Disconnect ${participantName}`}
                                                                title="Disconnect Participant"
                                                                onClick={() => disconnectParticipant(p.connectionId)}
                                                                style={{ width: '40px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#cc4b4bff', border: 'none' }}
                                                            >
                                                                <PhoneOff size={13} fill="white" aria-hidden="true" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted opacity-50">
                                        <Headphones size={50} className="mb-2" />
                                        <p style={{ textAlign: 'center' }}>Active conference participants will be visible here</p>
                                    </div>
                                )}
                            </>
                        )}
                        </>
                        )}
                    </div>
                </div>

                {/* 3. Controls & Info Section (Fixed Bottom) */}
                <div className="bg-white border-top border-2" style={{ flexShrink: 0, zIndex: 10 }}>

                    {isCallOnHold && (
                         <div className="alert d-flex align-items-center gap-3 p-3 m-3 rounded-3"
                            style={{ backgroundColor: '#fbc6a8', color: '#90361C', border: '2px solid #e29d89' }}>
                            <div className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{ width: 20, height: 20, backgroundColor: '#b8492a' }}>
                                <Info size={13} color="white" />
                            </div>
                            <div>
                                 <strong className="d-block">All participants are on hold.</strong>
                                <span className="fs-7">Tap Resume All to continue.</span>
                            </div>
                        </div>
                    )}

                    {/* Controls Row */}
                    <div className="px-4 py-3">
                        <div className="cw-controls-row d-flex justify-content-around mb-4" role="toolbar" aria-label="Call controls. Use Control Shift plus letter shortcuts.">
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <button
                                    ref={muteButtonRef}
                                    type="button"
                                    className={`cw-circle-btn ${isAgentMuted ? 'active' : ''}`}
                                    onClick={() => {
                                        toggleAgentMute()
                                        setLiveA11yAnnouncement(buildMuteToggleAnnouncement(!isAgentMuted))
                                    }}
                                    aria-label={isAgentMuted ? 'Unmute microphone' : 'Mute microphone'}
                                    aria-pressed={isAgentMuted}
                                    aria-keyshortcuts={CALL_A11Y.muteShortcut}
                                >
                                    {isAgentMuted ? <MicOff size={21} aria-hidden="true" /> : <Mic size={21} aria-hidden="true" />}
                                </button>
                                <div className="cw-btn-label" aria-hidden="true">Mute</div>
                            </div>

                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <button
                                    type="button"
                                    className={`cw-circle-btn ${isCallOnHold ? 'active-hold' : ''}`}
                                    onClick={() => {
                                        toggleCallHold()
                                        setLiveA11yAnnouncement(buildHoldToggleAnnouncement(!isCallOnHold))
                                    }}
                                    aria-label={isCallOnHold ? 'Resume all participants' : 'Hold all participants'}
                                    aria-pressed={isCallOnHold}
                                    aria-keyshortcuts={CALL_A11Y.holdShortcut}
                                    title={isCallOnHold ? 'Resume all participants' : 'Hold all participants'}
                                >
                                    {isCallOnHold ? <Play size={21} aria-hidden="true" /> : <Pause size={21} aria-hidden="true" />}
                                </button>
                                <div className="cw-btn-label" aria-hidden="true">{isCallOnHold ? 'Resume All' : 'Hold All'}</div>
                            </div>

                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <button
                                    type="button"
                                    className={`cw-circle-btn ${activeView === 'conference' ? 'conf-active' : ''}`}
                                    onClick={() => setActiveView(activeView === 'conference' ? 'none' : 'conference')}
                                    aria-label={activeView === 'conference' ? 'Close conference panel' : 'Open conference panel'}
                                    aria-pressed={activeView === 'conference'}
                                    aria-keyshortcuts={CALL_A11Y.conferenceShortcut}
                                >
                                    <Users size={21} aria-hidden="true" />
                                </button>
                                <div className="cw-btn-label" style={{ fontSize: '0.85rem' }} aria-hidden="true">Conference</div>
                            </div>

                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <button
                                    type="button"
                                    className={`cw-circle-btn ${activeView === 'keypad' ? 'active' : ''}`}
                                    onClick={() => setActiveView(activeView === 'keypad' ? 'none' : 'keypad')}
                                    aria-label={activeView === 'keypad' ? 'Close keypad' : 'Open keypad'}
                                    aria-pressed={activeView === 'keypad'}
                                    aria-keyshortcuts={CALL_A11Y.keypadShortcut}
                                >
                                    <Grip size={21} aria-hidden="true" />
                                </button>
                                <div className="cw-btn-label" aria-hidden="true">Keypad</div>
                            </div>

                        </div>

                        {/* End Call Button */}
                        <Button
                            variant="danger"
                            className="w-100 cw-action-btn d-flex align-items-center justify-content-center gap-2 shadow-sm"
                            style={{ backgroundColor: '#D7383C', border: 'none', height: '36px' }}
                            onClick={() => setIsEndCallConfirmOpen(true)}
                            aria-label="End call"
                            aria-keyshortcuts={CALL_A11Y.endCallShortcut}
                        >
                            <PhoneOff size={14} fill="currentColor" aria-hidden="true" />
                            <span className="fw-bold">End Call</span>
                        </Button>
                    </div>

                    {/* Call Info Accordion (Bottom-most) */}
                    <div className="border-top">
                        <div
                            role="button"
                            tabIndex={0}
                            className="d-flex justify-content-between align-items-center w-100 px-4 py-3 hover-bg-light"
                            onClick={() => setIsCallInfoOpen(!isCallInfoOpen)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    setIsCallInfoOpen(!isCallInfoOpen)
                                }
                            }}
                            aria-expanded={isCallInfoOpen}
                            aria-controls="cw-call-info-panel"
                            aria-labelledby="cw-call-info-label"
                            aria-keyshortcuts={CALL_A11Y.callInfoShortcut}
                            style={{ transition: 'background-color 0.2s' }}
                        >
                            <div className="d-flex align-items-center gap-2">
                                <div className="cw-icon-box-small rounded-circle" style={{ backgroundColor: '#DDDDDD' }} aria-hidden="true">
                                    <NotebookText size={11} className="text-dark" />
                                </div>
                                <span id="cw-call-info-label" className="font-monospace fs-7 text-dark">Call Info</span>
                            </div>
                            {isCallInfoOpen
                                ? <ChevronDown size={11} className="text-muted" aria-hidden="true" />
                                : <ChevronUp size={11} className="text-muted" aria-hidden="true" />}
                        </div>

                        {isCallInfoOpen && (
                            <div
                                id="cw-call-info-panel"
                                className="cw-accordion-content bg-light px-4 pb-3 cw-selectable"
                                style={{ borderTop: '1px solid #f0f0f0' }}
                            >
                                <div className="row g-2 pt-2">
                                    <div className="col-12">
                                        <div className="d-flex justify-content-between gap-2" style={{ minWidth: 0 }}>
                                            <span className="text-muted fs-7 flex-shrink-0">Call Type</span>
                                            <ClampWithTooltip
                                                text={activeCallData?.type ? activeCallData.type.replace('_', ' ') : (contact?.type || 'Voice')}
                                                className="fw-bold fs-7 text-dark font-monospace text-end"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 border-top pt-2 mt-2">
                                        <div className="d-flex justify-content-between gap-2" style={{ minWidth: 0 }}>
                                            <span className="text-muted fs-7 flex-shrink-0">Language</span>
                                            <ClampWithTooltip
                                                text={activeCallData?.language || 'EN-US'}
                                                className="fw-bold fs-7 text-dark font-monospace text-end"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 border-top pt-2 mt-2">
                                        <div className="d-flex justify-content-between gap-2" style={{ minWidth: 0 }}>
                                            <span className="text-muted fs-7 flex-shrink-0">Call ID</span>
                                            <ClampWithTooltip
                                                text={activeCallData?.callHeaderId || contact?.contactId || '...'}
                                                className="fw-bold fs-7 text-dark font-monospace text-end"
                                            />
                                        </div>
                                    </div>
                                    {contact?.contactArn && (
                                        <div className="col-12 border-top pt-2 mt-2">
                                            <div className="d-flex justify-content-between gap-2" style={{ minWidth: 0 }}>
                                                <span className="text-muted fs-7 flex-shrink-0">Contact ARN</span>
                                                <ClampWithTooltip
                                                    text={contact.contactArn}
                                                    className="fw-bold fs-8 text-dark font-monospace text-end"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    const hasAnyPermission =
        currentUser?.result?.opI_ShdTelephonic ||
        currentUser?.result?.opI_OndemandTelephonic ||
        currentUser?.result?.vrI_ShdVideoInteroreting ||
        currentUser?.result?.vrI_OndemandVideoInteroreting

    const isObserverTab = isMasterTab === false;
    const isElectionPending = isMasterTab === null;
    const observerCallActive = syncedCallActive;
    const observerStatusColor = getConnectStatusColor(syncedAgentState, observerCallActive);
    const observerStatusLabel = getConnectStatusLabel(syncedAgentState, observerCallActive);

    // Election still running (normally a few ms with Web Locks): never claim another tab
    // is master until that is confirmed — show progress instead.
    const renderElectingPill = () => (
        <div className={`cw-observer-pill-wrap cw-dock-${dockSide}`}>
            <div className='cw-observer-tooltip' role='tooltip' id='cw-electing-tooltip'>
                <span className='cw-observer-tooltip-title'>Connecting to Amazon Connect…</span>
                <span className='cw-observer-tooltip-hint mb-0'>
                    Checking whether this tab should run your Connect session.
                </span>
            </div>
            <button
                type='button'
                className='cw-float-btn collapsed cw-float-btn-pending'
                aria-label='Connecting to Amazon Connect'
                aria-describedby='cw-electing-tooltip'
                aria-busy='true'
            >
                <PillProgressRing state={pillRing} />
                <span className='cw-float-icon' aria-hidden='true'>
                    <Headphones size={17} color='#fff' />
                </span>
                <span
                    className='cw-status-dot-large'
                    style={{ backgroundColor: '#A1A5B7' }}
                    aria-hidden='true'
                />
            </button>
        </div>
    );

    const renderObserverPill = () => (
        <div className={`cw-observer-pill-wrap cw-dock-${dockSide}`}>
            <div className='cw-observer-tooltip' role='tooltip'>
                <span className='cw-observer-tooltip-hint'>
                    Switch to Master Tab to access the Connect widget.
                </span>
                <span className='cw-observer-tooltip-hint'>
                    Master tab has 🔹 prefix in the title.
                    {/* Master tab has 🔴 prefix in the title. */}
                </span>
                <div className='cw-observer-tooltip-status'>
                    <span
                        className='cw-observer-tooltip-dot'
                        style={{ backgroundColor: observerStatusColor }}
                    />
                    <span>{observerStatusLabel}</span>
                </div>
            </div>
            <button
                type='button'
                className={`cw-float-btn collapsed ${observerCallActive ? 'cw-blink-pill' : ''}`}
                onClick={requestMasterFocus}
                aria-label='Open Connect widget in master tab'
            >
                <span className='cw-float-icon' aria-hidden='true'>
                    <Headphones size={17} color='#fff' />
                </span>
                <span
                    className='cw-status-dot-large'
                    style={{ backgroundColor: observerStatusColor }}
                    aria-hidden='true'
                />
            </button>
        </div>
    );

    if (!hasAnyPermission) return null;

    // Interpreters must use the widget from a desktop; App.tsx already skips mounting
    // it on phones — this is a backstop.
    if (isMobilePhone()) return null;

    if (isElectionPending) {
        return renderElectingPill();
    }

    if (isObserverTab) {
        return renderObserverPill();
    }

    const renderContent = () => {
        const idleAuthPhase = bootPhase
        // Accepted + softphone failed is remembered immediately, but Test Connection
        // stays hidden until that contact is closed and destroyed.
        const deferSoftphoneRecovery =
            showSoftphoneRecoveryPanel &&
            !holdRecoveryUntilContactClosed &&
            !softphoneProbeInFlight &&
            idleAuthPhase !== 'idle'

        const renderIdleAuthShell = () => (
            <ConnectIdleShell
                phase={idleAuthPhase}
                ccpAuthState={ccpAuthState}
                isLoggedIn={isLoggedIn}
                isInitialized={isInitialized}
                agentName={agentName}
                agentEmail={agentEmail}
                agentStatus={agentStatus}
                statusColor={getStatusColor()}
                availabilityBlockReason={availabilityBlockReason}
                tokenPromptActive={tokenPromptActive}
                onLogin={handleLoginClick}
                onSignOut={handleCcpSignOut}
                onStatusToggle={handleStatusToggle}
                onAuthRecovery={handleAuthRecovery}
            />
        )

        const renderRecoveryPanel = () => (
            <ConnectSoftphoneRecoveryPanel
                probePhase={probePhase}
                probeError={probeError}
                onTestConnection={handleTestConnection}
                agentInitializationPending={!isInitialized || !isLoggedIn}
                softphoneErrorType={
                    softphoneFailure?.errorType ??
                    (lastSoftphoneErrorTypeRef.current as any)
                }
            />
        )

        // 3.5. CCP account does not match Adastra Connect login
        if (hasCcpEmailMismatch) {
            return (
                <ConnectAccountMismatchPanel
                    appUserEmail={appUserEmail}
                    onSignOut={handleMismatchSignOut}
                    isSigningOut={isMismatchSigningOut}
                />
            )
        }

        // Probe dial, connect, hangup, and contact clear stay on this panel.
        if (softphoneProbeInFlight) {
            return renderRecoveryPanel()
        }

        // 3.6. Network-drop auto-end — same layout as “Call Connected To Another User”.
        if (networkDropNotice) {
            return (
                <div className='text-center p-5 cw-fade-in-up h-100 d-flex flex-column justify-content-center'>
                    <div className='cw-buzz mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle shadow-sm'
                        style={{ width: 60, height: 60, backgroundColor: '#fffee8ff' }}>
                        <PhoneForwarded size={28} color='#A3A242' />
                    </div>
                    <h4 className='fw-bold text-danger mb-2'>Call Dropped.</h4>
                    <p className='text-muted fs-7 mb-4 px-3'>
                        Your connection dropped, so this call was routed to another available agent.
                    </p>
                    <p className='fw-bold fs-7 mb-4 d-flex align-items-center justify-content-center gap-2' style={{ color: '#A3A242' }}>
                        <NotebookText size={14} />
                        STAY TUNED FOR MORE CALLS
                    </p>
                    <Button
                        variant='secondary'
                        className='w-100 cw-action-btn fw-bold py-3'
                        style={{ backgroundColor: '#A3A242', border: 'none', color: '#fff' }}
                        onClick={dismissNetworkDropNotice}
                        aria-label="Dismiss call dropped notice"
                    >
                        Close
                    </Button>
                </div>
            )
        }

        // 4. After Call Work (ACW). Hidden for a probe until the agent leaves ACW.
        if (isACW && !probeAcwSuppressed) {
            const acwSecs = acwSecondsRemaining ?? ACW_AUTO_CLOSE_SECONDS
            return (
                <div className='text-center p-5 cw-fade-in-up h-100 d-flex flex-column justify-content-center'>
                    <div className='cw-icon-circle mb-4 bg-light-warning text-warning mx-auto' style={{ width: 50, height: 50 }}>
                        <CheckCircle size={25} />
                    </div>
                    <h4 className='fw-bold text-dark mb-2'>Call Ended</h4>
                    <p className='text-muted fs-7 mb-5 px-3'>You are in After Call Work status.<br />Complete your notes before closing.</p>
                    <Button
                        variant='warning'
                        className='w-100 cw-action-btn text-white fw-bold py-3 shadow-sm'
                        style={{ height: '48px', border: 'none', backgroundColor: '#F69100' }}
                        onClick={clearContact}
                        aria-label={
                            acwSecs > 0
                                ? `Close contact, auto-closes in ${acwSecs} seconds`
                                : 'Close contact'
                        }
                    >
                        Close Contact
                        {acwSecs > 0 && (
                            <span className='ms-2 opacity-90' style={{ fontSize: '0.9rem' }}>
                                ({acwSecs}s)
                            </span>
                        )}
                    </Button>
                </div>
            )
        }

        // 4.5. Test Connection only after the failed contact is closed and destroyed.
        // Until then, the connection-failed screen below stays up.
        if (!holdRecoveryUntilContactClosed && deferSoftphoneRecovery) {
            return renderIdleAuthShell()
        }
        if (!holdRecoveryUntilContactClosed && showSoftphoneRecoveryPanel) {
            return renderRecoveryPanel()
        }

        // 4.6. Connection failed during ringing/connecting (not during outbound probe UI)
        if (isConnectionFailed && !softphoneProbeInFlight) {
            return <ConnectConnectionFailedPanel failure={softphoneFailure} />
        }

        // 4.7. Connecting: call accepted by this terp, waiting for CCP to connect (prevents CallAcceptedByOther flicker).
        // Yields  to the ringer (5.5). Once a contact is genuinely ringing, the interpreter must have
        // the Accept button on screen; this spinner used to shadow it, leaving them watching
        // "Please wait" while the contact rang unanswered until the flow disconnected the caller.
        if (isAcceptedAndConnecting && !isConnected && !isIncomingCall) {
            return (
                <div
                    className='text-center p-5 cw-fade-in-up h-100 d-flex flex-column justify-content-center'
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    aria-labelledby="cw-connecting-title"
                >
                    <ConnectLoader />
                    <h4 id="cw-connecting-title" className='fw-bold text-dark mb-2'>Connecting...</h4>
                    <p className='text-muted fs-6 mb-2 px-3'>
                      Connecting your call. Keep the CCP open until you are connected.
                    </p>
                </div>
            )
        }



        // 5. Call accepted by another user (loser terp only; winner and idle never see this).
        // Never shown while a contact is ringing here — Connect's routing beats the backend broadcast.
        if (showCallAcceptedByOtherPopup && !isIncomingCall) {
            return (
                <div className='text-center p-5 cw-fade-in-up h-100 d-flex flex-column justify-content-center'>
                    <div className='cw-buzz mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle shadow-sm'
                        style={{ width: 60, height: 60, backgroundColor: '#fffee8ff' }}>
                        <PhoneForwarded size={28} color='#A3A242' />
                    </div>
                    <h4 className='fw-bold text-danger mb-2'>Call Connected To Another User</h4>
                    <p className='text-muted fs-7 mb-4 px-3'>Call has been successfully connected with an another available agent.</p>
                    <p className='fw-bold fs-7 mb-4 d-flex align-items-center justify-content-center gap-2' style={{ color: '#A3A242' }}>
                        <NotebookText size={14} />
                        STAY TUNED FOR MORE CALLS
                    </p>
                    <Button
                        variant='secondary'
                        className='w-100 cw-action-btn fw-bold py-3'
                        style={{ backgroundColor: '#A3A242', border: 'none', color: '#fff' }}
                        onClick={clearCallAcceptedByOther}
                        aria-label="Dismiss call connected to another user notice"
                    >
                        Close
                    </Button>
                </div>
            )
        }

        // 5.5. Incoming Call (Ringing) - Direct to CCP
        if (isIncomingCall) {
            return (
                <div
                    className='text-center p-5 cw-fade-in-up h-100 d-flex flex-column justify-content-center'
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cw-incoming-call-title"
                >
                    <div className='cw-recording-dot mb-4 mx-auto' style={{ width: '60px', height: '60px', animation: 'blinkRed 1s infinite' }} aria-hidden="true"></div>

                    <h4 id="cw-incoming-call-title" className='fw-bold text-dark mb-2'>Incoming Call...</h4>
                    <div className="cw-selectable w-100 px-3" style={{ maxWidth: '100%' }}>
                        <ClampWithTooltip text={callerInfo.company} className="fw-bold fs-5 text-dark mb-1" />
                        <ClampWithTooltip text={callerInfo.requester} className="text-muted fs-7 mb-4" />
                        {contact?.phoneNumber && (
                            <ClampWithTooltip text={contact.phoneNumber} className="text-dark fw-bold fs-6 mb-5" />
                        )}
                    </div>

                    <p className="fs-8 text-muted mb-3">
                        Shortcut: {CALL_A11Y.acceptHint} to accept, {CALL_A11Y.declineHint} to decline
                    </p>

                    <div className='d-flex gap-3 justify-content-center'>
                        <button
                            type="button"
                            className='btn btn-danger rounded-circle d-flex align-items-center justify-content-center shadow-lg'
                            style={{ width: '60px', height: '60px', backgroundColor: '#DC3545', border: 'none' }}
                            onClick={rejectCall}
                            aria-label="Decline call"
                            aria-keyshortcuts={CALL_A11Y.declineShortcut}
                        >
                            <PhoneOff size={24} fill="white" aria-hidden="true" />
                        </button>
                        <button
                            ref={acceptCallButtonRef}
                            type="button"
                            className='btn btn-success rounded-circle d-flex align-items-center justify-content-center shadow-lg'
                            style={{ width: '60px', height: '60px', backgroundColor: '#28A745', border: 'none' }}
                            onClick={acceptCall}
                            aria-label="Accept call"
                            aria-keyshortcuts={CALL_A11Y.acceptShortcut}
                        >
                            <Phone size={24} fill="white" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            )
        }

        // 6. Active Call
        if (isConnected && contact) {
            return renderActiveCall()
        }

        // 7. Idle / auth shell (shared layout for init, login, restoring, and idle)
        return renderIdleAuthShell()
    }

    return (
        <>
            {/* Floating Pill Toggle */}
            <div className={`cw-float-anchor cw-dock-${dockSide}`}>
                <button
                    type="button"
                    className={`cw-float-btn ${toggleExpand ? 'expanded' : 'collapsed'} ${!softphoneProbeInFlight && (isConnected || isIncomingCall || isAcceptedAndConnecting || isConnectionFailed) ? 'cw-blink-pill' : ''}`}
                    onClick={() => setToggleExpand(!toggleExpand)}
                    aria-label={
                        tokenPromptActive
                            ? 'Update your notification token to continue'
                            : toggleExpand
                                ? 'Collapse Connect panel'
                                : 'Open Connect panel'
                    }
                    aria-expanded={toggleExpand}
                    aria-controls="connect-widget-panel"
                    title={
                        tokenPromptActive
                            ? 'Update your notification token to continue'
                            : pillRing.mode !== 'hidden'
                                ? pillRing.label
                                : 'Open Connect Panel'
                    }
                    disabled={tokenPromptActive}
                    aria-busy={pillRing.mode !== 'hidden' && !(pillRing.mode === 'determinate' && pillRing.complete)}
                >
                    <PillProgressRing state={pillRing} />
                    <span className='cw-float-icon' aria-hidden="true">
                        {toggleExpand ? <ChevronUp size={20} color='#fff' /> : <Headphones size={17} color='#fff' />}
                    </span>
                    <span
                        className="cw-status-dot-large"
                        style={{ backgroundColor: getStatusColor() }}
                        aria-hidden="true"
                    ></span>
                </button>
            </div>

            {/* Outer wrapper — flex row; dock side from preferences */}
            <div className={`cw-panel-wrapper cw-dock-${dockSide}${!toggleExpand ? ' collapsed' : ''}`}>
                {/* Video Panel — LEFT side, slides in when video call active */}
                {videoConnectionInfo && videoCapabilities && (
                    <VideoCallPanel
                        videoConnectionInfo={videoConnectionInfo}
                        videoCapabilities={videoCapabilities}
                        isOpen={isVideoOpen}
                        isOnHold={isCallOnHold}
                        onClose={() => setIsVideoOpen(false)}
                    />
                )}

                {/* CCP Panel — RIGHT side, always present */}
                <div
                    id="connect-widget-panel"
                    ref={panelRef}
                    tabIndex={-1}
                    role="region"
                    aria-label="Adastra Connect call panel"
                    className={`connect-widget-panel ${!softphoneProbeInFlight && (isConnected || isIncomingCall || isAcceptedAndConnecting || isConnectionFailed) ? 'cw-active-call-glow' : ''}`}
                >
                    <div
                        className="cw-sr-only"
                        role="status"
                        aria-live="assertive"
                        aria-atomic="true"
                    >
                        {liveA11yAnnouncement}
                    </div>
                    <div className='cw-body d-flex flex-column cw-body-with-toolbar'>
                        <div className="cw-toolbar-strip">
                            <div className="cw-toolbar-strip-actions">
                            {!hasCcpEmailMismatch && (
                                <button
                                    type="button"
                                    className="cw-toolbar-settings-btn"
                                    title="Settings"
                                    aria-label="Open settings"
                                    onClick={() => setIsSettingsOpen(true)}
                                >
                                    <Settings size={16} strokeWidth={2.2} aria-hidden="true" />
                                </button>
                            )}
                            </div>
                        </div>
                    {softphoneFailure && !isConnectionFailed && !hasCcpEmailMismatch && (
                            <div className="cw-softphone-failure" role="alert">
                                <div className="cw-softphone-failure-text">
                                    <strong>
                                        <ClampWithTooltip text={softphoneFailure.title} />
                                    </strong>
                                    <ClampWithTooltip text={softphoneFailure.message} />
                                </div>
                                <div className="cw-softphone-failure-actions">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link text-white text-decoration-none p-0"
                                        aria-label="Dismiss softphone failure alert"
                                        onClick={clearSoftphoneFailure}
                                    >
                                        <X size={14} aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="cw-body-main flex-grow-1 d-flex flex-column overflow-hidden">
                            {isSettingsOpen && !hasCcpEmailMismatch ? (
                                <ConnectWidgetSettings
                                    voiceEnhancementMode={voiceEnhancementMode}
                                    microphoneDeviceId={microphoneDeviceId}
                                    speakerDeviceId={speakerDeviceId}
                                    microphones={microphones}
                                    speakers={speakers}
                                    isAudioEnhancementLocked={isConnected || isIncomingCall}
                                    dockSide={dockSide}
                                    onDockSideChange={writeWidgetDockSide}
                                    onBack={() => setIsSettingsOpen(false)}
                                    onRefreshDevices={refreshAudioDevices}
                                    onSetVoiceEnhancementMode={setVoiceEnhancementMode}
                                    onSetMicrophoneDevice={setMicrophoneDevice}
                                    onSetSpeakerDevice={setSpeakerDevice}
                                />
                            ) : (
                                renderContent()
                            )}
                        </div>
                    </div>
                    {isEndCallConfirmOpen && (
                        <div
                            className="cw-end-call-overlay"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="cw-end-call-title"
                        >
                            <div className="cw-end-call-dialog">
                                <div className="cw-end-call-icon">
                                    <PhoneOff size={22} strokeWidth={2.2} />
                                </div>
                                <h3 id="cw-end-call-title" className="cw-end-call-title">End the call?</h3>
                                <p className="cw-end-call-message">
                                    This will disconnect the call. Press Enter to confirm or Escape to cancel.
                                </p>
                                <div className="cw-end-call-actions">
                                    <button
                                        type="button"
                                        className="cw-end-call-cancel"
                                        onClick={() => setIsEndCallConfirmOpen(false)}
                                        disabled={isEndingCall}
                                        aria-label="Cancel ending call"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        ref={endCallConfirmActionRef}
                                        type="button"
                                        className="cw-end-call-action"
                                        onClick={handleConfirmEndCall}
                                        disabled={isEndingCall}
                                        aria-label={isEndingCall ? 'Ending call' : 'Confirm end call'}
                                    >
                                        <PhoneOff size={15} strokeWidth={2.2} aria-hidden="true" />
                                        <span>{isEndingCall ? 'Ending...' : 'End Call'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ConnectWidget
